import { useEffect, useState } from 'react';
import { ArrowLeft, CarFront, Trophy } from 'lucide-react';
import { ROUTES } from '../config.ts';
import { api, type HomeData, type LeaderboardData, type LeaderboardEntry, type LeaderboardPeriod } from '../services/api.ts';
import { Avatar } from './Avatar.tsx';
import { CloudBackground } from './CloudBackground.tsx';
import { GamesTopBar } from './GamesTopBar.tsx';

const PERIODS: Array<{ key: LeaderboardPeriod; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This week' },
  { key: 'all', label: 'All time' },
];

function formatDate(value: string): string {
  return new Intl.DateTimeFormat('en-NG', { day: 'numeric', month: 'short' }).format(new Date(value));
}

function RankRow({ entry, viewer }: { entry: LeaderboardEntry; viewer: boolean }) {
  const name = entry.user.name || (entry.user.username ? '@' + entry.user.username : 'My Genius player');
  return (
    <li className={`rank-row${viewer ? ' rank-row-viewer' : ''}`}>
      <span className={`rank-number${entry.rank === 1 ? ' rank-one' : ''}`}>{entry.rank}</span>
      <Avatar src={entry.user.avatar_url} name={name} size={34} />
      <span className="rank-person">
        <strong>{name}{viewer ? ' (You)' : ''}</strong>
        <span>Best run · {formatDate(entry.achieved_at)}</span>
      </span>
      <span className="rank-score"><strong>{entry.distance_metres.toLocaleString()}</strong><span>metres</span></span>
    </li>
  );
}

function RankSkeleton() {
  return <ul className="rank-list" aria-label="Loading leaderboard">
    {Array.from({ length: 7 }, (_, i) => <li className="rank-row rank-skeleton" key={i}><span /><span /><span /><span /></li>)}
  </ul>;
}

export function GamesLeaderboard({ home, navigate }: { home: HomeData; navigate: (path: string) => void }) {
  const [period, setPeriod] = useState<LeaderboardPeriod>('week');
  const [boards, setBoards] = useState<Partial<Record<LeaderboardPeriod, LeaderboardData>>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    if (!boards[period]) setLoading(true);
    api.leaderboard(period).then((data) => {
      if (!cancelled) { setBoards((current) => ({ ...current, [period]: data })); setLoading(false); }
    }).catch(() => {
      if (!cancelled) { setError('Could not load the leaderboard just now.'); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [period, retry]);

  const board = boards[period];
  const entries = board?.entries || [];
  const viewerInList = entries.some((entry) => entry.user.uuid === home.user.uuid);

  return (
    <div className="leaderboard-page">
      <CloudBackground />
      <GamesTopBar home={home} navigate={navigate} />
      <main className="leaderboard-main rail">
        <header className="leaderboard-heading">
          <button className="back-button" type="button" onClick={() => navigate(ROUTES.catalog)} aria-label="Back to games">
            <ArrowLeft size={21} />
          </button>
          <span className="leaderboard-title-icon" aria-hidden="true"><CarFront size={24} /></span>
          <span><p className="eyebrow">Smart Drive</p><h1>Leaderboard</h1></span>
        </header>

        <div className="period-tabs" role="group" aria-label="Leaderboard period">
          {PERIODS.map((item) => (
            <button
              key={item.key}
              type="button"
              aria-pressed={period === item.key}
              onClick={() => setPeriod(item.key)}
              className={period === item.key ? 'period-active' : undefined}
            >{item.label}</button>
          ))}
        </div>

        {loading && !board ? <RankSkeleton /> : error && !board ? (
          <div className="board-message"><p>{error}</p><button type="button" onClick={() => setRetry((n) => n + 1)}>Try again</button></div>
        ) : entries.length === 0 ? (
          <div className="board-message"><Trophy size={24} /><h2>No verified runs yet.</h2><p>Finish Smart Drive to set the first score.</p></div>
        ) : (
          <>
            <ul className="rank-list">
              {entries.map((entry) => <RankRow key={`${entry.rank}-${entry.user.uuid}`} entry={entry} viewer={entry.user.uuid === home.user.uuid} />)}
            </ul>
            {!viewerInList && board?.viewer && (
              <section className="your-rank" aria-label="Your rank"><p className="eyebrow">Your position</p><ul className="rank-list"><RankRow entry={board.viewer} viewer /></ul></section>
            )}
          </>
        )}

        <button className="board-play-button" type="button" onClick={() => navigate(ROUTES.smartDrive)}>
          Play Smart Drive <CarFront size={19} />
        </button>
      </main>
    </div>
  );
}
